import { useQuery } from '@tanstack/react-query';
import { whatsappNumber, type CustomerDTO, type CustomerRowDTO, type PosSettings, type SupervisorDTO } from '@bifeai/shared';
import { api } from './api';
import { formatMoney } from './format';

export type CustomerFilter = 'all' | 'debt' | 'overdue';

export function useCustomers(search: string, filter: CustomerFilter = 'all', archived = false) {
  const qs = new URLSearchParams();
  if (search.trim()) qs.set('search', search.trim());
  if (filter !== 'all') qs.set('filter', filter);
  if (archived) qs.set('archived', '1');
  return useQuery({
    queryKey: ['customers', 'list', search.trim(), filter, archived],
    queryFn: () => api.get<CustomerRowDTO[]>(`/customers?${qs.toString()}`),
    placeholderData: (prev) => prev,
  });
}

export function useCustomer(id: string | null) {
  return useQuery({
    queryKey: ['customers', 'detail', id],
    queryFn: () => api.get<CustomerDTO>(`/customers/${id}`),
    enabled: !!id,
  });
}

export function useSupervisors(enabled: boolean) {
  return useQuery({
    queryKey: ['customers', 'supervisors'],
    queryFn: () => api.get<SupervisorDTO[]>('/customers/supervisors'),
    enabled,
    staleTime: 60_000,
  });
}

export function usePosSettings() {
  return useQuery({
    queryKey: ['pos', 'settings'],
    queryFn: () => api.get<PosSettings>('/pos/settings'),
    staleTime: 60_000,
  });
}

/** Link de WhatsApp con el saldo listo para mandar. null si el teléfono no sirve. */
export function whatsappLink(c: { name: string; phone: string | null; balanceCents: number }, businessName: string): string | null {
  const n = whatsappNumber(c.phone);
  if (!n) return null;
  const first = c.name.split(' ')[0];
  const text =
    c.balanceCents > 0
      ? `Hola ${first}, te escribimos de ${businessName}. Tu saldo en cuenta corriente es de ${formatMoney(c.balanceCents)}. ¡Gracias!`
      : `Hola ${first}, te escribimos de ${businessName}. Tu cuenta corriente está al día. ¡Gracias!`;
  return `https://wa.me/${n}?text=${encodeURIComponent(text)}`;
}
